import type { Analytics } from 'firebase/analytics';
import type { FirebaseApp } from 'firebase/app';

import { isProduction } from '@/utils/envHelper';

type FirebaseConfig = {
  apiKey?: string;
  authDomain?: string;
  projectId?: string;
  storageBucket?: string;
  messagingSenderId?: string;
  appId?: string;
  measurementId?: string;
};

const getFirebaseConfig = (): FirebaseConfig => {
  const baseConfig: FirebaseConfig = {
    apiKey: process.env.NEXT_PUBLIC_FIREBASE_API_KEY,
    authDomain: process.env.NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN,
    projectId: process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID,
    storageBucket: process.env.NEXT_PUBLIC_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: process.env.NEXT_PUBLIC_FIREBASE_MESSAGING_SENDER_ID,
  };

  const envConfig: FirebaseConfig = isProduction
    ? {
        appId: process.env.NEXT_PUBLIC_FIREBASE_APP_ID,
        measurementId: process.env.NEXT_PUBLIC_FIREBASE_MEASUREMENT_ID,
      }
    : {
        appId: process.env.NEXT_PUBLIC_FIREBASE_APP_ID_TEST,
        measurementId: process.env.NEXT_PUBLIC_FIREBASE_MEASUREMENT_ID_TEST,
      };

  return {
    ...baseConfig,
    ...envConfig,
  };
};

const hasRequiredConfig = (config: FirebaseConfig) => {
  return Boolean(
    config.apiKey &&
    config.authDomain &&
    config.projectId &&
    config.appId &&
    config.measurementId,
  );
};

let firebaseApp: FirebaseApp | null = null;
let analyticsInstance: Analytics | null = null;
let initPromise: Promise<Analytics | null> | null = null;

export const initFirebaseAnalytics = async () => {
  if (typeof window === 'undefined') return null;
  if (analyticsInstance) return analyticsInstance;
  if (initPromise) return initPromise;

  const config = getFirebaseConfig();
  if (!hasRequiredConfig(config)) {
    console.warn('[firebase] Missing required config, skip init.');
    return null;
  }

  initPromise = (async () => {
    const { initializeApp, getApps } = await import('firebase/app');
    const { getAnalytics, isSupported, logEvent } = await import('firebase/analytics');

    const supported = await isSupported().catch(() => false);
    if (!supported) {
      console.warn('[firebase] Analytics is not supported in this environment.');
      return null;
    }

    const app = getApps().length ? getApps()[0] : initializeApp(config);
    firebaseApp = app;
    analyticsInstance = getAnalytics(app);
    if (!isProduction) {
      console.info(
        `[firebase] Analytics initialized. measurementId=${config.measurementId}`,
      );
      logEvent(analyticsInstance, 'dev_firebase_init', {
        debug_mode: true,
        env: process.env.NEXT_PUBLIC_ENV,
      });
    }
    return analyticsInstance;
  })();

  try {
    return await initPromise;
  } finally {
    initPromise = null;
  }
};

export const getFirebaseApp = () => firebaseApp;

export const logFirebaseEvent = async (
  eventName: string,
  params?: Record<string, any>,
) => {
  if (typeof window === 'undefined') return false;
  const analytics = await initFirebaseAnalytics();
  if (!analytics) return false;
  const { logEvent } = await import('firebase/analytics');
  const payload = isProduction
    ? params
    : {
        ...(params ?? {}),
        debug_mode: true,
        env: process.env.NEXT_PUBLIC_ENV,
      };
  logEvent(analytics, eventName, payload);
  return true;
};
